type NoticeKind = 'default' | 'success' | 'warning' | 'danger';
type NoticeShape = 'pill' | 'square';
type NoticePositionY = 'top' | 'bottom';
type NoticePositionX = 'start' | 'center' | 'end';

type NoticeAction = () => void;

interface NoticeParams {
  duration: number;
  speed: number;
  positionX: NoticePositionX;
  positionY: NoticePositionY;
  shape: NoticeShape;
}

interface NoticeButton {
  label: string;
  action: NoticeAction;
}

const DEFAULT_PARAMS: NoticeParams = {
  duration: 4000,
  speed: 250,
  positionX: 'end',
  positionY: 'top',
  shape: 'square',
};

const CONTAINER_CLASS = 'gdx-toast-host';

class NoticeBuilder {
  private title = '';
  private message = '';
  private kind: NoticeKind = 'default';
  private params: NoticeParams = { ...DEFAULT_PARAMS };
  private buttons: NoticeButton[] = [];
  private currentElement: HTMLElement | null = null;

  default(title: string | null, message?: string | null): this {
    return this.setNotice('default', title, message);
  }

  success(title: string | null, message?: string | null): this {
    return this.setNotice('success', title, message);
  }

  warning(title: string | null, message?: string | null): this {
    return this.setNotice('warning', title, message);
  }

  danger(title: string | null, message?: string | null): this {
    return this.setNotice('danger', title, message);
  }

  with(params: any): this {
    this.params = {
      ...this.params,
      duration: normalizeDuration(params.duration, this.params.duration),
      speed: normalizeDuration(params.speed, this.params.speed),
      positionX: normalizePositionX(params.positionX, this.params.positionX),
      positionY: normalizePositionY(params.positionY, this.params.positionY),
      shape: normalizeShape(params.shape, this.params.shape),
    };
    return this;
  }

  for(duration: number): this {
    this.params.duration = duration;
    return this;
  }

  as(shape: NoticeShape): this {
    this.params.shape = shape;
    return this;
  }

  from(
    positionY: NoticePositionY,
    positionX: NoticePositionX = this.params.positionX
  ): this {
    this.params.positionY = positionY;
    this.params.positionX = positionX;
    return this;
  }

  addButtons(buttons: Record<string, NoticeAction>): this {
    this.buttons = Object.entries(buttons).map(([label, action]) => ({
      label,
      action,
    }));
    return this;
  }

  hide(): void {
    if (this.currentElement) {
      removeNotice(this.currentElement, this.params.speed);
      this.currentElement = null;
    }
  }

  show(): void {
    if (typeof document === 'undefined') {
      return;
    }

    ensureHostStyles();
    const container = getContainer(
      this.params.positionY,
      this.params.positionX
    );
    const noticeElement = buildNoticeElement(
      this.kind,
      this.title,
      this.message,
      this.params,
      this.buttons,
      () => this.hide()
    );

    this.currentElement = noticeElement;
    container.appendChild(noticeElement);

    requestAnimationFrame(() => {
      noticeElement.dataset.state = 'open';
    });

    window.setTimeout(() => {
      if (noticeElement.isConnected) {
        removeNotice(noticeElement, this.params.speed);
      }
      if (this.currentElement === noticeElement) {
        this.currentElement = null;
      }
    }, this.params.duration);
  }

  private setNotice(
    kind: NoticeKind,
    title: string | null,
    message?: string | null
  ): this {
    this.kind = kind;

    if (message === undefined) {
      this.title = '';
      this.message = title ?? '';
      return this;
    }

    this.title = title ?? '';
    this.message = message ?? '';
    return this;
  }
}

function normalizeDuration(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizePositionX(
  value: unknown,
  fallback: NoticePositionX
): NoticePositionX {
  return value === 'start' || value === 'center' || value === 'end'
    ? value
    : fallback;
}

function normalizePositionY(
  value: unknown,
  fallback: NoticePositionY
): NoticePositionY {
  return value === 'top' || value === 'bottom' ? value : fallback;
}

function normalizeShape(value: unknown, fallback: NoticeShape): NoticeShape {
  return value === 'pill' || value === 'square' ? value : fallback;
}

function ensureHostStyles(): void {
  if (document.getElementById('gdx-toast-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'gdx-toast-styles';
  style.textContent = `
    .${CONTAINER_CLASS} {
      position: fixed;
      z-index: 3000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      pointer-events: none;
      width: min(420px, calc(100vw - 24px));
      box-sizing: border-box;
    }
    .${CONTAINER_CLASS}[data-y="top"] { top: 16px; }
    .${CONTAINER_CLASS}[data-y="bottom"] { bottom: 16px; }
    .${CONTAINER_CLASS}[data-x="start"] { left: 16px; align-items: flex-start; }
    .${CONTAINER_CLASS}[data-x="center"] { left: 50%; transform: translateX(-50%); align-items: center; }
    .${CONTAINER_CLASS}[data-x="end"] { right: 16px; align-items: flex-end; }
    .${CONTAINER_CLASS} .gdx-toast-notice {
      width: 100%;
      box-sizing: border-box;
      pointer-events: auto;
      box-shadow: 0 16px 32px rgba(15, 23, 42, 0.18);
      opacity: 0;
      transform: translateY(-12px);
      transition: opacity var(--gdx-toast-speed) ease, transform var(--gdx-toast-speed) ease;
      cursor: pointer;
      border: 1px solid rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }
    .${CONTAINER_CLASS}[data-y="bottom"] .gdx-toast-notice {
      transform: translateY(12px);
    }
    .${CONTAINER_CLASS} .gdx-toast-notice[data-state="open"] {
      opacity: 1;
      transform: translateY(0);
    }
    .${CONTAINER_CLASS} .gdx-toast-notice[data-shape="pill"] {
      border-radius: 999px;
    }
    .${CONTAINER_CLASS} .gdx-toast-notice[data-shape="square"] {
      border-radius: 14px;
    }
    .${CONTAINER_CLASS} .gdx-toast-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 16px;
    }
    .${CONTAINER_CLASS} .gdx-toast-icon {
      font-size: 18px;
      line-height: 1;
      margin-top: 2px;
      flex: 0 0 auto;
    }
    .${CONTAINER_CLASS} .gdx-toast-copy {
      min-width: 0;
      flex: 1 1 auto;
    }
    .${CONTAINER_CLASS} .gdx-toast-title {
      display: block;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .${CONTAINER_CLASS} .gdx-toast-message {
      display: block;
      line-height: 1.4;
      word-break: break-word;
    }
    .${CONTAINER_CLASS} .gdx-toast-actions {
      display: flex;
      gap: 8px;
      margin-top: 10px;
      flex-wrap: wrap;
    }
    .${CONTAINER_CLASS} .gdx-toast-action {
      background: rgba(255, 255, 255, 0.18);
      color: inherit;
      border: 0;
      border-radius: 999px;
      padding: 6px 10px;
      font: inherit;
      cursor: pointer;
    }
  `;

  document.head.appendChild(style);
}

function getContainer(
  positionY: NoticePositionY,
  positionX: NoticePositionX
): HTMLElement {
  const containerId = `gdx-toast-host-${positionY}-${positionX}`;
  const existing = document.getElementById(containerId);

  if (existing) {
    return existing;
  }

  const container = document.createElement('div');
  container.id = containerId;
  container.className = CONTAINER_CLASS;
  container.dataset.y = positionY;
  container.dataset.x = positionX;
  document.body.appendChild(container);
  return container;
}

function buildNoticeElement(
  kind: NoticeKind,
  title: string,
  message: string,
  params: NoticeParams,
  buttons: NoticeButton[],
  hide: () => void
): HTMLElement {
  const palette = getPalette(kind);
  const noticeElement = document.createElement('div');
  noticeElement.className = 'gdx-toast-notice';
  noticeElement.dataset.state = 'closed';
  noticeElement.dataset.shape = params.shape;
  noticeElement.style.background = palette.background;
  noticeElement.style.color = palette.color;
  noticeElement.style.setProperty('--gdx-toast-speed', `${params.speed}ms`);
  noticeElement.addEventListener('click', () => hide());

  const row = document.createElement('div');
  row.className = 'gdx-toast-row';

  const icon = document.createElement('span');
  icon.className = 'gdx-toast-icon';
  icon.textContent = palette.icon;

  const copy = document.createElement('div');
  copy.className = 'gdx-toast-copy';

  if (title.trim()) {
    const titleElement = document.createElement('strong');
    titleElement.className = 'gdx-toast-title';
    titleElement.textContent = title.trim();
    copy.appendChild(titleElement);
  }

  const messageElement = document.createElement('span');
  messageElement.className = 'gdx-toast-message';
  messageElement.textContent = message.trim() || title.trim();
  copy.appendChild(messageElement);

  if (buttons.length) {
    const actionRow = document.createElement('div');
    actionRow.className = 'gdx-toast-actions';

    for (const button of buttons) {
      const actionButton = document.createElement('button');
      actionButton.className = 'gdx-toast-action';
      actionButton.type = 'button';
      actionButton.textContent = button.label;
      actionButton.addEventListener('click', (event) => {
        event.stopPropagation();
        button.action();
      });
      actionRow.appendChild(actionButton);
    }

    copy.appendChild(actionRow);
  }

  row.appendChild(icon);
  row.appendChild(copy);
  noticeElement.appendChild(row);
  return noticeElement;
}

function removeNotice(noticeElement: HTMLElement, speed: number): void {
  noticeElement.dataset.state = 'closed';
  window.setTimeout(() => {
    noticeElement.remove();
  }, speed);
}

function getPalette(kind: NoticeKind): {
  background: string;
  color: string;
  icon: string;
} {
  switch (kind) {
    case 'success':
      return {
        background: 'linear-gradient(135deg, #166534, #15803d)',
        color: '#f0fdf4',
        icon: '✓',
      };
    case 'warning':
      return {
        background: 'linear-gradient(135deg, #b45309, #d97706)',
        color: '#fffbeb',
        icon: '!',
      };
    case 'danger':
      return {
        background: 'linear-gradient(135deg, #b91c1c, #dc2626)',
        color: '#fef2f2',
        icon: '!',
      };
    case 'default':
    default:
      return {
        background: 'linear-gradient(135deg, #1f2937, #111827)',
        color: '#f9fafb',
        icon: 'i',
      };
  }
}

export function toast(): NoticeBuilder {
  return new NoticeBuilder();
}

export function snackbar(): NoticeBuilder {
  return new NoticeBuilder().from('bottom', 'center');
}
